import { Steps, type StepProps } from "antd";
import { isEmpty } from "lodash";
import React, {
  Suspense,
  lazy,
  useContext,
  useEffect,
  useState,
  type ReactElement,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertComponent } from "shared/components/AlertComponent";
import { ReactComponent as FinishedIcon } from "../../../assets/images/check-circle-outline.svg";
import { ReactComponent as BasicInfoIcon } from "../../../assets/images/edit_note.svg";
import { ReactComponent as ApplicationsIcon } from "../../../assets/images/fertilizer.svg";
import { ReactComponent as YieldIcon } from "../../../assets/images/form-practices.svg";
import { ReactComponent as InfoIcon } from "../../../assets/images/information.svg";
import { ReactComponent as PracticesIcon } from "../../../assets/images/landscape.svg";
import { ReactComponent as Siol } from "../../../assets/images/soil.svg";
import { AssessmentStepsArray } from "../../../constants/Assesments";
import { GlobalContext } from "../../../context/AppContextProvider";
import { ASSESMENT_STEPS_ACTIONS } from "../../../context/actions/ActionTypes";
import { type IAssessmentReducer } from "../../../context/reducers/assessments";
import { Loader } from "../../../shared/components/Loader";
import { TitleAndSubTitle } from "../../../shared/components/Typography";
import {
  AssessmentStepDisplayNames,
  trackAssessmentJourney,
} from "../../../shared/utils/analyticsEvents";
import "./styles.less";
import { withWorkspaceId } from "shared/utils/workspaceRouting";

const Applications = lazy(
  async () => await import("./applications/Applications")
);
const BasicInfo = lazy(async () => await import("./basic-info/BasicInfo"));
const Practices = lazy(async () => await import("./practices/Practices"));
const SoilCharecteristics = lazy(async () => await import("./soil/Soil"));
const Yield = lazy(async () => await import("./yield/Yield"));

const StepComponent = (): ReactElement => {
  const { t } = useTranslation();
  const {
    assessmentStepsDispatch,
    assessmentStepsState,
    assessmentState,
    orgState,
  } = useContext(GlobalContext);
  const { currentStep, enabledSteps } = assessmentStepsState;

  const { customStep, assessmentId = "", orgId = "" } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("Create a new custom assessment");
  useEffect(() => {
    if (customStep) {
      const idx = AssessmentStepsArray.indexOf(customStep as any);
      const currentStepIndex = idx === -1 ? 0 : idx;
      assessmentStepsDispatch({
        type: ASSESMENT_STEPS_ACTIONS.UPDATE_CURRENT_STEP,
        payload: currentStepIndex,
      });
    }
    if (globalThis.location.pathname.includes("edit")) {
      setTitle("Edit Assessment");
    } else if (globalThis.location.pathname.includes("create")) {
      setTitle("Create a new custom assessment");
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(assessmentState?.currentAssessment)) {
      const {
        applications,
        practices,
        soilCharacteristics,
        yield: yieldData,
      }: any = assessmentState?.currentAssessment as IAssessmentReducer;
      /**
       * @constant:isDataAvailalable, old assessments may not have country and city so we are enabling only if the data exists
       *  */
      const isDataAvailalable =
        assessmentState?.currentAssessment.farms.country;
      const setEnableSteps = {
        applications: Boolean(applications && isDataAvailalable),
        practices: Boolean(practices && isDataAvailalable),
        soilCharacteristics: Boolean(soilCharacteristics && isDataAvailalable),
        yield: Boolean(yieldData && isDataAvailalable),
      };
      assessmentStepsDispatch({
        type: ASSESMENT_STEPS_ACTIONS.UPDATE_ENABLE_STEPS,
        payload: setEnableSteps,
      });
    }
  }, [assessmentState?.currentAssessment]);

  useEffect(() => {
    const currentAssessment: any = assessmentState?.currentAssessment;
    if (isEmpty(currentAssessment) || isEmpty(orgState?.currentOrg)) return;
    trackAssessmentJourney({
      assessmentName: currentAssessment?.assessmentName,
      status: currentAssessment?.assessmentStatus,
      stepName: AssessmentStepDisplayNames[AssessmentStepsArray[currentStep]],
      cfpCropPathway: currentAssessment?.pathway,
      crop: currentAssessment?.cropName,
      year: currentAssessment?.cropCycleStartDate
        ? new Date(currentAssessment.cropCycleStartDate).getFullYear()
        : undefined,
      workspaceId: orgState?.currentOrg?.workspace_id,
      organizationName: orgState?.currentOrg?.name,
    });
  }, [
    currentStep,
    assessmentState?.currentAssessment,
    orgState?.currentOrg?.workspace_id,
    orgState?.currentOrg?.name,
  ]);

  const handleStepChange = (current: number): void => {
    const customStep = AssessmentStepsArray[current];
    const url =
      assessmentId != null
        ? `edit/${assessmentId}/custom/${customStep}`
        : `create/custom/${customStep}`;
    navigate(withWorkspaceId(`/app/organization/${orgId}/assessment/${url}`));
    assessmentStepsDispatch({
      type: ASSESMENT_STEPS_ACTIONS.UPDATE_CURRENT_STEP,
      payload: current,
    });
  };

  const stepitems: StepProps[] = [
    {
      title: t("Basic info"),
      icon: currentStep > 0 ? <FinishedIcon /> : <BasicInfoIcon />,
      disabled: false,
    },
    {
      title: t("Applications"),
      icon: currentStep > 1 ? <FinishedIcon /> : <ApplicationsIcon />,
      disabled: !enabledSteps.applications,
    },
    {
      title: t("Practices & machinery usage"),
      icon: currentStep > 2 ? <FinishedIcon /> : <PracticesIcon />,
      disabled: !enabledSteps.practices,
    },

    {
      title: t("Soil characteristics"),
      icon: currentStep > 3 ? <FinishedIcon /> : <Siol />,
      disabled: !enabledSteps.soilCharacteristics,
    },
    {
      title: t("Yield"),
      icon: <YieldIcon />,
      disabled: !enabledSteps.yield,
    },
  ];

  const stepComponents: ReactElement[] = [
    <BasicInfo key={0} />,
    <Applications key={1} />,
    <Practices key={2} />,
    <SoilCharecteristics key={3} />,
    <Yield key={4} />,
  ];

  return (
    <div className="custom">
      <TitleAndSubTitle titleFontSize={20} title={title} />
      <Steps
        items={stepitems}
        className="custom__steps"
        labelPlacement="vertical"
        current={currentStep}
        onChange={handleStepChange}
      />
      {currentStep === 0 && (
        <AlertComponent
          closable
          type="info"
          icon={<InfoIcon />}
          message="The fields with blue background indicate data imported from other Cropwise Products."
        />
      )}
      <Suspense fallback={<Loader mask />}>
        {stepComponents[currentStep]}
      </Suspense>
    </div>
  );
};

export default StepComponent;
